/**
 * ConflictResolutionDrawer — operator-facing UI for blocked outbox items.
 *
 * Opens when an item has status='conflict' (server returned 4xx that
 * isn't auth/rate-limit). Operator can:
 *   1. Retry — push the item back to pending (e.g. after refreshing stock)
 *   2. Discard — abandon the action
 *   3. View payload — see the raw request body
 *
 * For known error codes (INSUFFICIENT_STOCK), we surface a tailored
 * message with helpful context (available qty, etc.).
 */
import React, { useEffect, useState } from "react";
import { AlertTriangle, RefreshCcw, Trash2, Eye, EyeOff } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getById, resetConflict, discardItem } from "@/offline/outbox";
import type { OutboxItem } from "@/offline/db";
import { useOffline } from "@/context/OfflineProvider";

interface Props {
  itemId: string | null;
  onClose: () => void;
}

export const ConflictResolutionDrawer: React.FC<Props> = ({
  itemId,
  onClose,
}) => {
  const { syncNow } = useOffline();
  const [item, setItem] = useState<OutboxItem | null>(null);
  const [showPayload, setShowPayload] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!itemId) {
      setItem(null);
      setShowPayload(false);
      return;
    }
    getById(itemId).then((found) => setItem(found ?? null));
  }, [itemId]);

  if (!itemId) return null;

  const errorCode =
    typeof item?.lastErrorDetail === "object" && item?.lastErrorDetail
      ? ((item.lastErrorDetail as Record<string, unknown>).code as
          | string
          | undefined) ||
        ((item.lastErrorDetail as Record<string, unknown>).errorCode as
          | string
          | undefined)
      : undefined;

  const handleRetry = async () => {
    if (!item) return;
    setBusy(true);
    try {
      await resetConflict(item.id);
      toast.success("Trying again");
      await syncNow();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to retry");
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = async () => {
    if (!item) return;
    if (
      !window.confirm(
        "Discard this sale? It won't be recorded, and its cash will show as extra when the drawer is counted.",
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await discardItem(item.id);
      toast.success("Sale discarded");
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to discard");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={!!itemId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <SheetTitle>This sale wasn't recorded yet</SheetTitle>
          </div>
          <SheetDescription>
            It was saved on this till while offline, but the system couldn't accept it. Fix the cause, then try again.
          </SheetDescription>
        </SheetHeader>

        {!item ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Loading…
          </p>
        ) : (
          <div className="mt-5 space-y-5">
            {/* Headline */}
            <div className="space-y-1">
              <p className="text-base font-semibold">{item.summary}</p>
              <div className="flex items-center gap-2 text-xs flex-wrap">
                <span className="text-muted-foreground">
                  {item.attempts} attempt{item.attempts === 1 ? "" : "s"}
                </span>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground">
                  Saved on this till {new Date(item.createdAt).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Tailored explanation per known error */}
            <ErrorExplanation errorCode={errorCode} item={item} />

            {/* Raw error */}
            {item.lastError && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1">
                  What the system said
                </p>
                <p className="text-sm text-amber-900 font-medium">
                  {item.lastError}
                </p>
              </div>
            )}

            {/* Payload viewer */}
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPayload((v) => !v)}
                className="gap-2 px-0"
              >
                {showPayload ? (
                  <EyeOff className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
                {showPayload ? "Hide" : "Show"} details for support
              </Button>
              {showPayload && (
                <pre className="mt-2 text-[10px] bg-muted/30 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(item.payload, null, 2)}
                </pre>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Button
                variant="outline"
                onClick={handleDiscard}
                disabled={busy}
                className="text-rose-600 hover:text-rose-700"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Discard
              </Button>
              <Button
                onClick={handleRetry}
                disabled={busy}
                className="ml-auto bg-[#4CAF50] hover:bg-[#45A049] text-white"
              >
                <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
                Try again
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ── Tailored explanation per error code ─────────────────────────

const Box: React.FC<{ title: string; children?: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm space-y-1 dark:border-rose-900 dark:bg-rose-950/40">
    <p className="font-medium text-rose-900 dark:text-rose-200">{title}</p>
    {children && <div className="text-xs text-rose-700 dark:text-rose-300 space-y-1">{children}</div>}
  </div>
);

const ErrorExplanation: React.FC<{
  errorCode?: string;
  item: OutboxItem;
}> = ({ errorCode, item }) => {
  const d = ((item.lastErrorDetail as Record<string, unknown> | null)?.details ?? {}) as Record<string, unknown>;
  if (errorCode === "INSUFFICIENT_STOCK") {
    const kg = d.soldBy === "weight";
    const fmtQ = (n: unknown) => (typeof n === "number" ? (kg ? `${(n / 1000).toFixed(3)} kg` : String(n)) : "?");
    return (
      <Box title={`The system doesn't have enough ${String(d.name || "of a product")} for this sale.`}>
        <p>
          This sale needs <strong>{fmtQ(d.requested)}</strong> · the system has <strong>{fmtQ(d.available)}</strong>.
        </p>
        <p>
          The goods did leave the shelf, so the stock figure was off. Ask a manager to receive the stock or correct it with a
          stock take, then tap <strong>Try again</strong>.
        </p>
      </Box>
    );
  }
  if (errorCode === "PRODUCT_NOT_FOUND") {
    return (
      <Box title={`A product in this sale (${String(d.name || "unknown")}) was deleted.`}>
        <p>Ring the sale up again with a current product, then discard this one.</p>
      </Box>
    );
  }
  if (errorCode === "OFFLINE_TOO_OLD") {
    return (
      <Box title="This sale is more than 30 days old.">
        <p>Check it with your manager. If it's real, enter it again as a new sale and discard this one.</p>
      </Box>
    );
  }
  if (errorCode === "OFFLINE_NOT_ALLOWED") {
    return <Box title="Only fully-paid cash sales can be made without internet." />;
  }
  return null;
};

export default ConflictResolutionDrawer;
